import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import Header from './components/Header';
import Footer from './components/Footer';
import { useMotion } from './hooks/useMotion';
import { ChevronUp } from 'lucide-react';
import './App.css';

const Home = lazy(() => import('./pages/Home'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Products = lazy(() => import('./pages/Products'));
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const ServiceDetail = lazy(() => import('./pages/ServiceDetail'));
const Industries = lazy(() => import('./pages/Industries'));
const Process = lazy(() => import('./pages/Process'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const Testimonials = lazy(() => import('./pages/Testimonials'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Contact = lazy(() => import('./pages/Contact'));

const WHATSAPP_PHONE_NUMBER = '966579468040';

const WhatsAppIcon: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M16 5C10.5 5 6 9.3 6 14.6C6 16.9 6.8 19 8.3 20.7L7 25L11.6 23.7C13.1 24.5 14.6 25 16.2 25C21.7 25 26 20.7 26 15.4C26 10.1 21.5 5 16 5Z"
      stroke="white"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.4 13.2C13.2 12.8 12.9 12.4 12.5 12.4C12.2 12.4 11.8 12.4 11.5 12.4C11.1 12.4 10.7 12.9 10.7 13.5C10.7 14.1 11.1 15.1 12 16.1C12.9 17.2 14.4 18.3 16.2 18.8C16.9 19 17.4 19.1 17.8 19.1C18.3 19.1 18.6 18.8 18.8 18.4C19 18 19.2 17.5 19.2 17.2C19.2 16.8 18.9 16.6 18.6 16.5C18.3 16.4 17.7 16.1 17.3 15.9C16.9 15.8 16.7 15.8 16.5 16.1C16.3 16.3 16 16.6 15.7 16.5C15 16.2 14.2 15.6 13.8 15.1C13.6 14.8 13.7 14.6 13.8 14.5C14 14.3 14.2 14.1 14.3 13.9C14.4 13.7 14.4 13.5 14.3 13.3C14.1 12.8 13.7 13 13.4 13.2Z"
      fill="white"
    />
  </svg>
);

const FloatingActions: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 200);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const openWhatsApp = () => {
    const message = encodeURIComponent('Hi, I am interested in your services.');
    const url = `https://wa.me/${WHATSAPP_PHONE_NUMBER}?text=${message}`;
    window.open(url, '_blank');
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="floating-actions">
      <button
        className="floating-btn floating-btn-whatsapp"
        onClick={openWhatsApp}
        aria-label="Chat on WhatsApp"
      >
        <WhatsAppIcon size={30} />
      </button>
      <button
        className="floating-btn floating-btn-top"
        onClick={scrollToTop}
        aria-label="Back to top"
      >
        <ChevronUp size={24} />
      </button>
    </div>
  );
};

function MotionHandler() {
  useMotion();
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Fix: when navigating via React Router, scroll stays at previous position -> shows bottom of new page
    // Always start at top, instantly (no smooth) for user-friendly feel, then focus main for a11y
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
    // fallback for browsers that don't support instant
    window.scrollTo(0, 0);
    document.getElementById('main')?.focus?.();
  }, [pathname]);
  return null;
}

function App() {
  return (
    <LanguageProvider>
      <Router>
        <MotionHandler />
        <ScrollToTop />
        <div className="App">
          <Header />
          <main>
            <Suspense fallback={<div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center', color: 'var(--muted)', fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: '.14em', textTransform: 'uppercase' }}>Loading…</div>}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/services" element={<Services />} />
                <Route path="/services/:serviceKey" element={<ServiceDetail />} />
                <Route path="/products" element={<Products />} />
                <Route path="/industries" element={<Industries />} />
                <Route path="/process" element={<Process />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/portfolio" element={<Portfolio />} />
                <Route path="/testimonials" element={<Testimonials />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/product/:productKey" element={<ProductDetail />} />

              </Routes>
            </Suspense>
          </main>
          <FloatingActions />
          <Footer />
        </div>
      </Router>
    </LanguageProvider>
  );
}

export default App;
